import { useState } from 'react';
import { Button } from '@/components/ui/Button';
import { Download, Save, Trash2, Copy, Send, RefreshCw } from 'lucide-react';

export default function DemosPage() {
  const [loadingStates, setLoadingStates] = useState<Record<string, boolean>>({});

  const simulateAction = (id: string) => {
    setLoadingStates(prev => ({ ...prev, [id]: true }));
    setTimeout(() => {
      setLoadingStates(prev => ({ ...prev, [id]: false }));
    }, 2000);
  };

  return (
    <div className="p-8 max-w-7xl mx-auto">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-slate-900 mb-2">Demos de Componentes</h1>
        <p className="text-slate-600">
          Esta sección muestra los componentes de UI disponibles en la aplicación.
        </p>
      </div>

      <div className="space-y-12">
        <section>
          <h2 className="text-2xl font-bold text-slate-900 mb-6">Botones - Variantes</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            <div className="space-y-4 p-6 bg-white rounded-lg border border-slate-200">
              <h3 className="font-semibold text-slate-700 mb-3">Primary / Default</h3>
              <Button variant="primary">Botón Primary</Button>
              <Button
                variant="primary"
                isLoading={loadingStates['primary']}
                onClick={() => simulateAction('primary')}
              >
                {loadingStates['primary'] ? 'Guardando...' : 'Guardar'}
              </Button>
            </div>

            <div className="space-y-4 p-6 bg-white rounded-lg border border-slate-200">
              <h3 className="font-semibold text-slate-700 mb-3">Destructive / Danger</h3>
              <Button variant="destructive">Botón Destructivo</Button>
              <Button
                variant="danger"
                isLoading={loadingStates['danger']}
                onClick={() => simulateAction('danger')}
              >
                <Trash2 size={16} />
                {loadingStates['danger'] ? 'Eliminando...' : 'Eliminar'}
              </Button>
            </div>

            <div className="space-y-4 p-6 bg-white rounded-lg border border-slate-200">
              <h3 className="font-semibold text-slate-700 mb-3">Success</h3>
              <Button variant="success">Botón Success</Button>
              <Button
                variant="success"
                isLoading={loadingStates['success']}
                onClick={() => simulateAction('success')}
              >
                {loadingStates['success'] ? 'Procesando...' : 'Aprobar'}
              </Button>
            </div>

            <div className="space-y-4 p-6 bg-white rounded-lg border border-slate-200">
              <h3 className="font-semibold text-slate-700 mb-3">Secondary</h3>
              <Button variant="secondary">Botón Secondary</Button>
              <Button
                variant="secondary"
                isLoading={loadingStates['secondary']}
                onClick={() => simulateAction('secondary')}
              >
                {loadingStates['secondary'] ? 'Cancelando...' : 'Cancelar'}
              </Button>
            </div>

            <div className="space-y-4 p-6 bg-white rounded-lg border border-slate-200">
              <h3 className="font-semibold text-slate-700 mb-3">Outline</h3>
              <Button variant="outline">Botón Outline</Button>
              <Button
                variant="outline"
                isLoading={loadingStates['outline']}
                onClick={() => simulateAction('outline')}
              >
                {loadingStates['outline'] ? 'Actualizando...' : 'Actualizar'}
              </Button>
            </div>

            <div className="space-y-4 p-6 bg-white rounded-lg border border-slate-200">
              <h3 className="font-semibold text-slate-700 mb-3">Ghost</h3>
              <Button variant="ghost">Botón Ghost</Button>
              <Button
                variant="ghost"
                isLoading={loadingStates['ghost']}
                onClick={() => simulateAction('ghost')}
              >
                {loadingStates['ghost'] ? 'Cerrando...' : 'Cerrar'}
              </Button>
            </div>

            <div className="space-y-4 p-6 bg-white rounded-lg border border-slate-200">
              <h3 className="font-semibold text-slate-700 mb-3">Link</h3>
              <Button variant="link">Botón Link</Button>
              <Button
                variant="link"
                isLoading={loadingStates['link']}
                onClick={() => simulateAction('link')}
              >
                {loadingStates['link'] ? 'Cargando...' : 'Ver más'}
              </Button>
            </div>
          </div>
        </section>

        <section>
          <h2 className="text-2xl font-bold text-slate-900 mb-6">Botones - Tamaños</h2>
          <div className="p-6 bg-white rounded-lg border border-slate-200">
            <div className="flex flex-wrap items-end gap-4">
              <div className="space-y-2">
                <p className="text-sm text-slate-600 font-medium">Small (sm)</p>
                <Button size="sm">Botón Pequeño</Button>
              </div>
              <div className="space-y-2">
                <p className="text-sm text-slate-600 font-medium">Medium (md) - Default</p>
                <Button size="md">Botón Mediano</Button>
              </div>
              <div className="space-y-2">
                <p className="text-sm text-slate-600 font-medium">Large (lg)</p>
                <Button size="lg">Botón Grande</Button>
              </div>
              <div className="space-y-2">
                <p className="text-sm text-slate-600 font-medium">Icon</p>
                <Button size="icon">
                  <Save size={18} />
                </Button>
              </div>
            </div>
          </div>
        </section>

        <section>
          <h2 className="text-2xl font-bold text-slate-900 mb-6">Botones con Iconos y Loading</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="p-6 bg-white rounded-lg border border-slate-200 space-y-4">
              <h3 className="font-semibold text-slate-700 mb-3">Con Iconos</h3>
              <div className="flex flex-wrap gap-3">
                <Button variant="primary">
                  <Save size={16} />
                  Guardar
                </Button>
                <Button variant="destructive">
                  <Trash2 size={16} />
                  Eliminar
                </Button>
                <Button variant="secondary">
                  <Download size={16} />
                  Descargar
                </Button>
                <Button variant="outline">
                  <Copy size={16} />
                  Copiar
                </Button>
              </div>
            </div>

            <div className="p-6 bg-white rounded-lg border border-slate-200 space-y-4">
              <h3 className="font-semibold text-slate-700 mb-3">Estados de Loading</h3>
              <div className="flex flex-wrap gap-3">
                <Button
                  variant="primary"
                  isLoading={loadingStates['save']}
                  onClick={() => simulateAction('save')}
                >
                  <Save size={16} />
                  {loadingStates['save'] ? 'Guardando...' : 'Guardar'}
                </Button>
                <Button
                  variant="success"
                  isLoading={loadingStates['send']}
                  onClick={() => simulateAction('send')}
                >
                  <Send size={16} />
                  {loadingStates['send'] ? 'Enviando...' : 'Enviar'}
                </Button>
                <Button
                  variant="outline"
                  isLoading={loadingStates['refresh']}
                  onClick={() => simulateAction('refresh')}
                >
                  <RefreshCw size={16} />
                  {loadingStates['refresh'] ? 'Actualizando...' : 'Refrescar'}
                </Button>
              </div>
            </div>
          </div>
        </section>

        <section>
          <h2 className="text-2xl font-bold text-slate-900 mb-6">Casos de Uso Reales</h2>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="p-6 bg-white rounded-lg border border-slate-200">
              <h3 className="font-semibold text-slate-700 mb-4">Formulario de Miembro</h3>
              <div className="space-y-4">
                <div className="flex gap-3">
                  <Button
                    variant="primary"
                    isLoading={loadingStates['form-save']}
                    onClick={() => simulateAction('form-save')}
                  >
                    <Save size={16} />
                    {loadingStates['form-save'] ? 'Guardando...' : 'Guardar Cambios'}
                  </Button>
                  <Button variant="secondary">
                    Cancelar
                  </Button>
                </div>
              </div>
            </div>

            <div className="p-6 bg-white rounded-lg border border-slate-200">
              <h3 className="font-semibold text-slate-700 mb-4">Confirmación de Eliminación</h3>
              <div className="space-y-4">
                <p className="text-sm text-slate-600">¿Estás seguro de eliminar este registro?</p>
                <div className="flex gap-3">
                  <Button
                    variant="destructive"
                    isLoading={loadingStates['delete-confirm']}
                    onClick={() => simulateAction('delete-confirm')}
                  >
                    <Trash2 size={16} />
                    {loadingStates['delete-confirm'] ? 'Eliminando...' : 'Sí, Eliminar'}
                  </Button>
                  <Button variant="outline">
                    No, Cancelar
                  </Button>
                </div>
              </div>
            </div>

            <div className="p-6 bg-white rounded-lg border border-slate-200">
              <h3 className="font-semibold text-slate-700 mb-4">Enviar Credenciales</h3>
              <div className="space-y-4">
                <div className="flex gap-3">
                  <Button
                    variant="success"
                    isLoading={loadingStates['credentials']}
                    onClick={() => simulateAction('credentials')}
                    className="flex-1"
                  >
                    <Send size={16} />
                    {loadingStates['credentials'] ? 'Copiando y Guardando...' : 'Copiar y Guardar'}
                  </Button>
                </div>
              </div>
            </div>

            <div className="p-6 bg-white rounded-lg border border-slate-200">
              <h3 className="font-semibold text-slate-700 mb-4">Acciones Múltiples</h3>
              <div className="space-y-3">
                <Button
                  variant="primary"
                  className="w-full"
                  isLoading={loadingStates['action1']}
                  onClick={() => simulateAction('action1')}
                >
                  {loadingStates['action1'] ? 'Procesando...' : 'Acción Principal'}
                </Button>
                <Button
                  variant="outline"
                  className="w-full"
                  isLoading={loadingStates['action2']}
                  onClick={() => simulateAction('action2')}
                >
                  {loadingStates['action2'] ? 'Exportando...' : 'Exportar Datos'}
                </Button>
                <Button
                  variant="ghost"
                  className="w-full"
                >
                  Ver Historial
                </Button>
              </div>
            </div>
          </div>
        </section>

        <section>
          <h2 className="text-2xl font-bold text-slate-900 mb-6">Estados Especiales</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="p-6 bg-white rounded-lg border border-slate-200 space-y-4">
              <h3 className="font-semibold text-slate-700 mb-3">Deshabilitado</h3>
              <Button disabled variant="primary">Botón Deshabilitado</Button>
              <Button disabled variant="destructive">Eliminar Deshabilitado</Button>
              <Button disabled variant="outline">Outline Deshabilitado</Button>
            </div>

            <div className="p-6 bg-white rounded-lg border border-slate-200 space-y-4">
              <h3 className="font-semibold text-slate-700 mb-3">Loading Permanente</h3>
              <Button isLoading variant="primary">Cargando...</Button>
              <Button isLoading variant="success">Procesando...</Button>
              <Button isLoading variant="outline">Actualizando...</Button>
            </div>

            <div className="p-6 bg-white rounded-lg border border-slate-200 space-y-4">
              <h3 className="font-semibold text-slate-700 mb-3">Ancho Completo</h3>
              <Button className="w-full" variant="primary">Botón Completo</Button>
              <Button
                className="w-full"
                variant="success"
                isLoading={loadingStates['full-width']}
                onClick={() => simulateAction('full-width')}
              >
                {loadingStates['full-width'] ? 'Guardando...' : 'Guardar Todo'}
              </Button>
            </div>
          </div>
        </section>
      </div>
    </div>
  );
}
